import React from 'react';

import { Auth0Provider } from '@auth0/auth0-react';
import { ThemeProvider, CssBaseline, ThemeOptions } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter as Router, Route, Navigate, Routes } from 'react-router-dom';

import { client } from './client';
import ProtectedRoute from './components/common/ProtectedRoute/ProtectedRoute';
import { ROUTES } from './config/routes';
import DataIssuesPage from './pages/DataIssuesPage/DataIssues';
import FixBuyerByProductPage from './pages/FixDataPages/FixBuyerByProductPage';
import FixCustomerGroupPage from './pages/FixDataPages/FixCustomerGroupPage';
import FixProductGroupPage from './pages/FixDataPages/FixProductGroupPage';
import LandingPage from './pages/LandingPage/LandingPage';
import LoginPage from './pages/LoginPage/LoginPage';
import BuyerByProductSyncPage from './pages/SheetsSyncPages/BuyerByProductSyncPage';
import BuyerVendorSyncPage from './pages/SheetsSyncPages/BuyerVendorSyncPage';
import CustomerGroupSyncPage from './pages/SheetsSyncPages/CustomerGroupSyncPage';
import DateMappingSyncPage from './pages/SheetsSyncPages/DateMappingSyncPage';
import ProductGroupSyncPage from './pages/SheetsSyncPages/ProductGroupSyncPage';
import SalesmenSyncPage from './pages/SheetsSyncPages/SalesmenSyncPage';
import SalesRepDepartmentSyncPage from './pages/SheetsSyncPages/SalesRepDepartmentSyncPage';
import CreditReportUploadPage from './pages/Uploads/CreditReportUploadPage';
import TransactionsUploadPage from './pages/Uploads/TransactionsUploadPage';
import { AuthProvider } from './services/auth/AuthContext';

export const themeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
  },
};

const theme = createTheme(themeOptions); // You can customize this theme as needed

// Define the shape of your environment variables
interface EnvVariables {
  REACT_APP_AUTH0_DOMAIN?: string;
  REACT_APP_AUTH0_CLIENT_ID?: string;
  REACT_APP_AUTH0_AUDIENCE?: string;
  REACT_APP_API_URL?: string;
}

// Safely access environment variables
const env: EnvVariables = {
  REACT_APP_AUTH0_DOMAIN: process.env.REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_CLIENT_ID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_AUDIENCE: process.env.REACT_APP_AUTH0_AUDIENCE,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
};

// Ensure required variables are defined
if (!env.REACT_APP_AUTH0_DOMAIN || !env.REACT_APP_AUTH0_CLIENT_ID) {
  throw new Error('Missing required environment variables for Auth0');
}

const Auth0Config = {
  domain: env.REACT_APP_AUTH0_DOMAIN,
  clientId: env.REACT_APP_AUTH0_CLIENT_ID,
  authorizationParams: {
    audience: env.REACT_APP_AUTH0_AUDIENCE,
  },
  redirectUri: window.location.origin,
};

const queryClient = new QueryClient();

// configure internal service client
client.setConfig({
  // set default base url for requests
  baseUrl: env.REACT_APP_API_URL,
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Auth0Provider {...Auth0Config}>
        <ThemeProvider theme={theme}>
          <SnackbarProvider />
          <CssBaseline />
          <Router>
            <AuthProvider>
              <Routes>
                <Route path={ROUTES.LOGIN.path} element={<LoginPage />} />
                <Route
                  path={ROUTES.HOME.path}
                  element={<ProtectedRoute element={<LandingPage />} />}
                />
                <Route
                  path={ROUTES.BUYER_VENDOR.path}
                  element={<ProtectedRoute element={<BuyerVendorSyncPage />} />}
                />
                <Route
                  path={ROUTES.BUYER_BY_PRODUCT.path}
                  element={<ProtectedRoute element={<BuyerByProductSyncPage />} />}
                />
                <Route
                  path={ROUTES.DATE_MAPPING.path}
                  element={<ProtectedRoute element={<DateMappingSyncPage />} />}
                />
                <Route
                  path={ROUTES.TRANSACTIONS.path}
                  element={<ProtectedRoute element={<TransactionsUploadPage />} />}
                />
                <Route
                  path={ROUTES.CREDIT_REPORT.path}
                  element={<ProtectedRoute element={<CreditReportUploadPage />} />}
                />
                <Route
                  path={ROUTES.CUSTOMER_GROUP.path}
                  element={<ProtectedRoute element={<CustomerGroupSyncPage />} />}
                />
                <Route
                  path={ROUTES.PRODUCT_GROUP.path}
                  element={<ProtectedRoute element={<ProductGroupSyncPage />} />}
                />
                <Route
                  path={ROUTES.SALES_REP_DEPARTMENT.path}
                  element={<ProtectedRoute element={<SalesRepDepartmentSyncPage />} />}
                />
                <Route
                  path={ROUTES.SALESMEN.path}
                  element={<ProtectedRoute element={<SalesmenSyncPage />} />}
                />
                <Route
                  path={ROUTES.DATA_ISSUES.path}
                  element={<ProtectedRoute element={<DataIssuesPage />} />}
                />
                <Route
                  path={ROUTES.FIX_BUYER_BY_PRODUCT.path}
                  element={<ProtectedRoute element={<FixBuyerByProductPage />} />}
                />
                <Route
                  path={ROUTES.FIX_PRODUCT_GROUP.path}
                  element={<ProtectedRoute element={<FixProductGroupPage />} />}
                />
                <Route
                  path={ROUTES.FIX_CUSTOMER_GROUP.path}
                  element={<ProtectedRoute element={<FixCustomerGroupPage />} />}
                />
                <Route path="*" element={<Navigate to={ROUTES.HOME.path} replace />} />
              </Routes>
            </AuthProvider>
          </Router>
        </ThemeProvider>
      </Auth0Provider>
    </QueryClientProvider>
  );
}

export default App;
